<template>
    <div class="article-privacy">
        <content-subheading heading="1. Introduction" marginBottom="mb-4"></content-subheading>
        <p>We are committed to safeguarding the privacy of our website visitors and service users.</p>
        <p>
            This policy applies where we are acting as a data controller with respect to the personal data of our
            website visitors and service users; in other words, where we determine the purposes and means of the
            processing of that personal data.
        </p>
        <p>
            We use cookies on our website. Insofar as those cookies are not strictly necessary for the provision of our
            website and services, we will ask you to consent to our use of cookies when you first visit our website.
        </p>
        <p>
            In this policy, "we", "us" and "our" refer to {{ $config.public.companyNameLong }}. For more information
            about us, see Section 17.
        </p>
        <content-subheading heading="2. How we use your personal data" marginBottom="mb-4"></content-subheading>
        <p>In this Section 2 we have set out:</p>
        <ul>
            <li>the general categories of personal data that we may process;</li>
            <li>the purposes for which we may process personal data; and</li>
            <li>the legal bases of the processing.</li>
        </ul>
        <p>How we use your personal data depends on your relationship with us:</p>
        <ul>
            <li>
                <strong>Website Account Holder</strong> - any user of the website who has an account on the website.
            </li>
            <li>
                <strong>Website Visitor</strong> - any user of the website who does not have an account, or has an
                account but is not logged in.
            </li>
        </ul>
        <p>
            We may process the <strong>User Account</strong> of a <strong>Website Account Holder</strong> - namely the
            information you provide to create or edit your user account, which may include name, email, telephone,
            profile picture, profile description, occupation, company name and your interest in the website. Your
            account data may be shared with our staff to assist with account or service enquiries. Personal data may
            also be processed in the delivery of our private messaging system.
        </p>
        <p>The legal basis for this processing is <em>Consent</em>.</p>
        <p>
            We may process the <strong>Website Sessions</strong> of a <strong>Website Account Holder</strong> - namely
            technical data about users logged in to the website including IP address, device information, and a last
            access date. This personal data is processed to allow users to login to the website and close inactive
            sessions.
        </p>
        <p>The legal basis for this processing is <em>Legitimate Interest</em>.</p>
        <p>
            We may process the <strong>Website Logs</strong> of a <strong>Website Account Holder</strong> - namely
            technical data about use of the website including IP address, pages served, actions taken and error
            messages. This personal data is processed to reduce errors, develop the user experience and improve security
            of personal data.
        </p>
        <p>The legal basis for this processing is <em>Legitimate Interest</em>.</p>
        <p>
            We may process the <strong>System Emails</strong> of a <strong>Website Account Holder</strong> - namely
            technical data about emails sent by the website to account holders, which is processed to maintain a record
            of system generated communication with users to reduce errors and improve user experience. This data is
            never shared with a third party.
        </p>
        <p>The legal basis for this processing is <em>Legitimate Interest</em>.</p>
        <p>
            We may process the <strong>Website Logs</strong> of a <strong>Website Visitor</strong> - namely technical
            data about use of the website including IP address, pages served, actions taken and error messages. This
            personal data is processed to reduce errors, develop the user experience and improve security of personal
            data.
        </p>
        <p>The legal basis for this processing is <em>Legitimate Interest</em>.</p>
        <p>
            We may process any of your personal data identified in this policy where necessary for the establishment,
            exercise or defence of legal claims, whether in court proceedings or in an administrative or out-of-court
            procedure. The legal basis for this processing is our legitimate interests, namely the protection and
            assertion of our legal rights, your legal rights and the legal rights of others.
        </p>
        <p>
            In addition to the specific purposes for which we may process your personal data set out in this Section 2,
            we may also process any of your personal data where such processing is necessary for compliance with a legal
            obligation to which we are subject, or in order to protect your vital interests or the vital interests of
            another natural person.
        </p>
        <p>Please do not supply any other person's personal data to us, unless we prompt you to do so.</p>
        <content-subheading
            heading="3. Providing your personal data to others"
            marginBottom="mb-4"
        ></content-subheading>
        <p>
            In addition to the specific disclosures of personal data set out in this Section 3, we may disclose your
            personal data where such disclosure is necessary for compliance with a legal obligation to which we are
            subject, or in order to protect your vital interests or the vital interests of another natural person. We
            may also disclose your personal data where such disclosure is necessary for the establishment, exercise or
            defence of legal claims, whether in court proceedings or in an administrative or out-of-court procedure.
        </p>
        <content-subheading
            heading="4. International transfers of your personal data"
            marginBottom="mb-4"
        ></content-subheading>
        <p>
            In this Section 4, we provide information about the circumstances in which your personal data may be
            transferred to countries outside the European Economic Area (EEA).
        </p>
        <p>The hosting facilities for our website are situated in the United Kingdom.</p>
        <p>
            You acknowledge that personal data that you submit for publication through our website or services may be
            available, via the internet, around the world. We cannot prevent the use (or misuse) of such personal data
            by others.
        </p>
        <content-subheading heading="5. Retaining and deleting personal data" marginBottom="mb-4"></content-subheading>
        <p>
            This Section 5 sets out our data retention policies and procedure, which are designed to help ensure that we
            comply with our legal obligations in relation to the retention and deletion of personal data.
        </p>
        <p>
            Personal data that we process for any purpose or purposes shall not be kept for longer than is necessary for
            that purpose or those purposes.
        </p>
        <p>We will retain your personal data as follows:</p>
        <ul>
            <li>
                The <strong>User Account</strong> of a <strong>Website Account Holder</strong> will be retained until
                the user withdraws consent.
            </li>
            <li>
                The <strong>Website Sessions</strong> of a <strong>Website Account Holder</strong> will be retained for
                1 month.
            </li>
            <li>
                The <strong>Website Logs</strong> of a <strong>Website Account Holder</strong> will be retained for 2
                years.
            </li>
            <li>
                The <strong>System Emails</strong> of a <strong>Website Account Holder</strong> will be retained for 3
                months.
            </li>
            <li>
                The <strong>Website Logs</strong> of a <strong>Website Visitor</strong> will be retained for 1 year.
            </li>
        </ul>
        <p>
            Notwithstanding the other provisions of this Section 5, we may retain your personal data where such
            retention is necessary for compliance with a legal obligation to which we are subject, or in order to
            protect your vital interests or the vital interests of another natural person.
        </p>
        <content-subheading heading="6. Security of personal data" marginBottom="mb-4"></content-subheading>
        <p>
            We will take appropriate technical and organisational precautions to secure your personal data and to
            prevent the loss, misuse or alteration of your personal data.
        </p>
        <p>
            We will store all your personal data on secure servers, personal computers and mobile devices, and in secure
            manual record-keeping systems.
        </p>
        <p>The following personal data will be stored by us in encrypted form: password(s).</p>
        <p>
            Data relating to browsing requests that is sent from your web browser to our web server, or from our web
            server to your web browser, will be protected using encryption technology.
        </p>
        <p>
            You acknowledge that the transmission of unencrypted (or inadequately encrypted) data over the internet is
            inherently insecure, and we cannot guarantee the security of data sent over the internet.
        </p>
        <p>
            You should ensure that your password is not susceptible to being guessed, whether by a person or a computer
            program. You are responsible for keeping the password you use for accessing our website confidential and we
            will not ask you for your password (except when you log in to our website).
        </p>
        <content-subheading heading="7. Amendments" marginBottom="mb-4"></content-subheading>
        <p>We may update this policy from time to time by publishing a new version on our website.</p>
        <p>You should check this page occasionally to ensure you are happy with any changes to this policy.</p>
        <p>
            We may notify you of significant changes to this policy by email or through the private messaging system on
            our website.
        </p>
        <content-subheading heading="8. Your rights" marginBottom="mb-4"></content-subheading>
        <p>
            In this Section 8, we have summarised the rights that you have under data protection law. Some of the rights
            are complex, and not all of the details have been included in our summaries. Accordingly, you should read
            the relevant laws and guidance from the regulatory authorities for a full explanation of these rights.
        </p>
        <ul>
            <li>the right to access;</li>
            <li>the right to rectification;</li>
            <li>the right to erasure;</li>
            <li>the right to restrict processing;</li>
            <li>the right to object to processing;</li>
            <li>the right to data portability;</li>
            <li>the right to complain to a supervisory authority; and</li>
            <li>the right to withdraw consent.</li>
        </ul>
        <p>
            You have the right to confirmation as to whether or not we process your personal data and, where we do,
            access to the personal data, together with certain additional information. That additional information
            includes details of the purposes of the processing, the categories of personal data concerned and the
            recipients of the personal data. Providing the rights and freedoms of others are not affected, we will
            supply to you a copy of your personal data. The first copy will be provided free of charge, but additional
            copies may be subject to a reasonable fee.
        </p>
        <p>
            You have the right to have any inaccurate personal data about you rectified and, taking into account the
            purposes of the processing, to have any incomplete personal data about you completed.
        </p>
        <p>
            In some circumstances you have the right to the erasure of your personal data without undue delay. Those
            circumstances include: the personal data are no longer necessary in relation to the purposes for which they
            were collected or otherwise processed; you withdraw consent to consent-based processing; you object to the
            processing under certain rules of applicable data protection law; the processing is for direct marketing
            purposes; and the personal data have been unlawfully processed. However, there are exclusions of the right
            to erasure. The general exclusions include where processing is necessary: for exercising the right of
            freedom of expression and information; for compliance with a legal obligation; or for the establishment,
            exercise or defence of legal claims.
        </p>
        <p>
            In some circumstances you have the right to restrict the processing of your personal data. Those
            circumstances are: you contest the accuracy of the personal data; processing is unlawful but you oppose
            erasure; we no longer need the personal data for the purposes of our processing, but you require personal
            data for the establishment, exercise or defence of legal claims; and you have objected to processing,
            pending the verification of that objection. Where processing has been restricted on this basis, we may
            continue to store your personal data. However, we will only otherwise process it: with your consent; for the
            establishment, exercise or defence of legal claims; for the protection of the rights of another natural or
            legal person; or for reasons of important public interest.
        </p>
        <p>
            You have the right to object to our processing of your personal data on grounds relating to your particular
            situation, but only to the extent that the legal basis for the processing is that the processing is
            necessary for: the performance of a task carried out in the public interest or in the exercise of any
            official authority vested in us; or the purposes of the legitimate interests pursued by us or by a third
            party. If you make such an objection, we will cease to process the personal information unless we can
            demonstrate compelling legitimate grounds for the processing which override your interests, rights and
            freedoms, or the processing is for the establishment, exercise or defence of legal claims.
        </p>
        <p>
            You have the right to object to our processing of your personal data for direct marketing purposes
            (including profiling for direct marketing purposes). If you make such an objection, we will cease to process
            your personal data for this purpose.
        </p>
        <p>
            You have the right to object to our processing of your personal data for scientific or historical research
            purposes or statistical purposes on grounds relating to your particular situation, unless the processing is
            necessary for the performance of a task carried out for reasons of public interest.
        </p>
        <p>To the extent that the legal basis for our processing of your personal data is:</p>
        <ul>
            <li>consent; or</li>
            <li>
                that the processing is necessary for the performance of a contract to which you are party or in order to
                take steps at your request prior to entering into a contract,
            </li>
        </ul>
        <p>
            and such processing is carried out by automated means, you have the right to receive your personal data from
            us in a structured, commonly used and machine-readable format. However, this right does not apply where it
            would adversely affect the rights and freedoms of others.
        </p>
        <p>
            If you consider that our processing of your personal information infringes data protection laws, you have a
            legal right to lodge a complaint with a supervisory authority responsible for data protection. You may do so
            in the EU member state of your habitual residence, your place of work or the place of the alleged
            infringement.
        </p>
        <p>
            To the extent that the legal basis for our processing of your personal information is consent, you have the
            right to withdraw that consent at any time. Withdrawal will not affect the lawfulness of processing before
            the withdrawal.
        </p>
        <p>You may exercise any of your rights in relation to your personal data by using our website contact form.</p>
        <content-subheading heading="9. Third party websites" marginBottom="mb-4"></content-subheading>
        <p>Our website includes hyperlinks to, and details of, third party websites.</p>
        <p>
            We have no control over, and are not responsible for, the privacy policies and practices of third parties.
        </p>
        <content-subheading heading="10. Personal data of children" marginBottom="mb-4"></content-subheading>
        <p>Our website and services are targeted at persons over the age of 18.</p>
        <p>
            If we have reason to believe that we hold personal data of a person under that age in our databases, we will
            delete that personal data.
        </p>
        <content-subheading heading="11. Updating information" marginBottom="mb-4"></content-subheading>
        <p>Please let us know if the personal information that we hold about you needs to be corrected or updated.</p>
        <content-subheading heading="12. About cookies" marginBottom="mb-4"></content-subheading>
        <p>
            A cookie is a file containing an identifier (a string of letters and numbers) that is sent by a web server
            to a web browser and is stored by the browser. The identifier is then sent back to the server each time the
            browser requests a page from the server.
        </p>
        <p>
            Cookies may be either "persistent" cookies or "session" cookies: a persistent cookie will be stored by a web
            browser and will remain valid until its set expiry date, unless deleted by the user before the expiry date;
            a session cookie, on the other hand, will expire at the end of the user session, when the web browser is
            closed.
        </p>
        <p>
            Cookies do not typically contain any information that personally identifies a user, but personal information
            that we store about you may be linked to the information stored in and obtained from cookies.
        </p>
        <content-subheading heading="13. Cookies that we use" marginBottom="mb-4"></content-subheading>
        <p>
            Please consult our
            <nuxt-link to="/cookies">cookies</nuxt-link> page for information about the cookies we use.
        </p>
        <content-subheading
            heading="14. Cookies used by our service providers"
            marginBottom="mb-4"
        ></content-subheading>
        <p>
            Please consult our
            <nuxt-link to="/cookies">cookies</nuxt-link> page for information about how our service providers use
            cookies.
        </p>
        <content-subheading heading="15. Managing cookies" marginBottom="mb-4"></content-subheading>
        <p>
            Most browsers allow you to refuse to accept cookies and to delete cookies. The methods for doing so vary
            from browser to browser, and from version to version. You can however obtain up-to-date information about
            blocking and deleting cookies via these links:
        </p>
        <ul>
            <li>
                <a href="https://support.google.com/chrome/answer/95647?hl=en"
                    >https://support.google.com/chrome/answer/95647?hl=en</a
                >
                (Chrome);
            </li>
            <li>
                <a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences"
                    >https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences</a
                >
                (Firefox);
            </li>
            <li>
                <a href="http://www.opera.com/help/tutorials/security/cookies/"
                    >http://www.opera.com/help/tutorials/security/cookies/</a
                >
                (Opera);
            </li>
            <li>
                <a href="https://support.microsoft.com/en-gb/help/17442/windows-internet-explorer-delete-manage-cookies"
                    >https://support.microsoft.com/en-gb/help/17442/windows-internet-explorer-delete-manage-cookies</a
                >
                (Internet Explorer);
            </li>
            <li>
                <a href="https://support.apple.com/kb/PH21411">https://support.apple.com/kb/PH21411</a>
                (Safari); and
            </li>
            <li>
                <a href="https://privacy.microsoft.com/en-us/windows-10-microsoft-edge-and-privacy"
                    >https://privacy.microsoft.com/en-us/windows-10-microsoft-edge-and-privacy</a
                >
                (Edge).
            </li>
        </ul>
        <p>Blocking all cookies will have a negative impact upon the usability of many websites.</p>
        <p>If you block cookies, you will not be able to use all the features on our website.</p>
        <content-subheading heading="16. Cookie preferences" marginBottom="mb-4"></content-subheading>
        <p>
            You can manage your preferences relating to the use of cookies on our website by visiting our
            <nuxt-link to="/cookies">cookies</nuxt-link> page.
        </p>
        <content-subheading heading="17. Our details" marginBottom="mb-4"></content-subheading>
        <p>
            This website is owned and operated by {{ $config.public.companyNameLong }}. You can contact us using our
            website <nuxt-link to="/contact">contact form</nuxt-link>.
        </p>
    </div>
</template>

<script>
export default {
    name: 'ArticlePrivacy',
};
</script>
